import useProfileTab from "../hooks/useProfileTab";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IosShareOutlined as IosShareOutlinedIcon } from "./icons/FigmaControlIcons";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useLocation } from "react-router-dom";

import ProfileHeader from "./TabsHeader";
import StatsSlider from "./StatsSlider";
import EntityBackgroundTasks from "./EntityBackgroundTasks";
import ListTableTabsTemplate from "./layouts/ListTableTabsTemplate";

import QuickActionsCard from "./sidebar-widgets/QuickActionsCard";
import { openInterviewScheduler } from "../utils/interviewScheduling";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import AIAgentCard from "./sidebar-widgets/AIAgentCard";
import JobTopMatchesCard from "./sidebar-widgets/JobTopMatchesCard";
import JobOwnerCard from "./sidebar-widgets/JobOwnerCard";
import JobNotesCard from "./sidebar-widgets/JobNotesCard";
import JobDetailsContent from "./job-details/JobDetailsContent";
import ShareJobDialog from "./ShareJobDialog";
import { buildPublicSharePayload, createAndCopyPublicShareLink, SHARE_LINK_COPIED_MESSAGE } from "../utils/publicShareLinks";
import JobOwnerDialog from "./JobOwnerDialog";
import AddCandidateDialog from "./PopupWindows/AddCandidateDialog";
import AddToSequenceDialog from "./PopupWindows/AddToSequenceDialog";
import { TAB_VIEW_CONTENT_WRAPPER_SX, TAB_VIEW_HEADER_SX } from "./tabViewContentSpacing";
import { MatchActionIcon, SequenceActionIcon } from "./ActionButtonIcons";
import AssignTeamPillIcon from "./icons/AssignTeamPillIcon";
import TeamMemberAddIcon from "./icons/TeamMemberAddIcon";
import { formatEmploymentType } from "../utils/jobEmploymentTypes";
import {
  JOB_CANDIDATE_STAT_ITEMS,
  isJobCandidateStatMatch,
} from "../utils/jobCandidateFilters";
import {
  getRecruiterOwnerName,
  isActivePendingFlag,
  sanitizeJobCompanyContactFields,
} from "../utils/recruiterOwnership";
import { fetchRecruiterUsers } from "../utils/teamMembers";
import {
  formatEntityAddedDateText,
  resolveEntityAddedDate,
} from "../utils/entityDateText";
import { formatLocalDate } from "../utils/dateTime";
import { syncJobBackgroundWork } from "../utils/entityBackgroundWorkState";
import { normalizeLocationFilterValues } from "../utils/locationFilters";
import { buildJobSequenceItem } from "../utils/sequenceItems";
import { JOB_STATUS_OPTIONS } from "../utils/jobStatusOptions";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const BORDER = "#E5E7EB";
const ADMIN_ROLES = new Set(["admin", "administrator", "system_user"]);
const JOB_CANDIDATE_DELETE_ROLES = new Set(["recruiter", "admin", "administrator", "system_user"]);
const JOB_STAGE_CLEAR_VALUE = "all";
const JOB_STATUS_SELECT_OPTIONS = JOB_STATUS_OPTIONS;
const JOB_STATUS_DISPLAY_BY_KEY = {
  draft: "Draft",
  pending: "Draft",
  "for review": "Draft",
  scheduled: "Draft",
  open: "Open",
  active: "Open",
  "accepting candidates": "Open",
  approved: "Open",
  "in progress": "Open",
  "on hold": "On Hold",
  onhold: "On Hold",
  filled: "Filled",
  "closed ( filled )": "Filled",
  placed: "Filled",
  completed: "Filled",
  closed: "Closed",
  "closed ( canceled )": "Closed",
  "closed ( cancelled )": "Closed",
  rejected: "Closed",
  archived: "Closed",
};
const normalizeJobStatusDisplay = (value, fallback = "") => {
  const raw = String(value ?? "").trim();
  return JOB_STATUS_DISPLAY_BY_KEY[raw.toLowerCase()] || raw || fallback;
};
const JOB_STAGE_FILTER_OPTIONS = [
  { value: "applied", label: "Applied" },
  { value: "matched", label: "Matched" },
  { value: "sourced", label: "Sourced" },
  { value: "contacted", label: "Contacted" },
  { value: "screened", label: "Screened" },
  { value: "presented", label: "Presented" },
  { value: "interview", label: "Interview" },
  { value: "assessment", label: "Assessment" },
  { value: "offer", label: "Offer" },
  { value: "hired", label: "Hired" },
  { value: "rejected", label: "Rejected" },
];
const JOB_STAGE_LABEL_BY_VALUE = JOB_STAGE_FILTER_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const normalizeJobStageFilterValue = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === JOB_STAGE_CLEAR_VALUE || raw === "no stage") {
    return JOB_STAGE_CLEAR_VALUE;
  }
  if (raw === "interviewing" || raw === "interview") {
    return "interview";
  }
  return Object.prototype.hasOwnProperty.call(JOB_STAGE_LABEL_BY_VALUE, raw)
    ? raw
    : JOB_STAGE_CLEAR_VALUE;
};

const resolvePersistedJobStageValue = (jobRow = {}) =>
  normalizeJobStageFilterValue(jobRow?.job_stage || jobRow?.pipeline_stage || jobRow?.stage || "");

const isManualJobCandidateRow = (row = {}) =>
  String(row?.match_source || "")
    .trim()
    .toLowerCase() === "manual";
const isAiJobCandidateRow = (row = {}) => !isManualJobCandidateRow(row);

const normalizeTab = (t) => {
  const v = String(t || "").toLowerCase();
  const ok = ["details", "stages", "matches", "activities"];
  return ok.includes(v) ? v : "details";
};

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("authToken") ||
  localStorage.getItem("jwt") ||
  localStorage.getItem("accessToken") ||
  "";

const getCurrentUserRole = () => {
  const directRole = String(localStorage.getItem("role") || "").trim().toLowerCase();
  if (directRole) return directRole;

  try {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return "";

    const parsedUser = JSON.parse(storedUser);
    return String(parsedUser?.role || "").trim().toLowerCase();
  } catch {
    return "";
  }
};

const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .filter(Boolean)
          .map((item) => String(item).trim())
          .filter(Boolean);
      }
    } catch {}

    return trimmed
      .split(/\s*(?:,|\|)\s*/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeDraftTextValue = (value) => {
  const nextValue = String(value ?? "").trim();
  return nextValue === "-" ? "" : nextValue;
};

const normalizeComparableName = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const uniqTextValues = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );

const toFiniteCount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getCandidateRowId = (row = {}) => {
  const numericCandidateId = Number(row?.candidate_id ?? row?.candidateId ?? row?.id ?? 0);
  return Number.isFinite(numericCandidateId) && numericCandidateId > 0
    ? numericCandidateId
    : null;
};

const applyStageToCandidateRows = (
  rows = [],
  candidateIds = [],
  nextStage = "",
  moveOptions = {}
) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const normalizedStage = String(nextStage || "").trim();
  const isRejectedStage = normalizedStage.toLowerCase() === "rejected";
  const normalizedRejectedBy = String(moveOptions?.rejectedBy || "").trim();
  const normalizedRejectionReason = String(moveOptions?.rejectionReason || "").trim();
  const normalizedRejectionNote = String(moveOptions?.rejectionNote || "").trim();
  const candidateIdSet = new Set(
    (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
  );

  if (!candidateIdSet.size || !normalizedStage) {
    return safeRows;
  }

  return safeRows.map((row) => {
    const candidateId = getCandidateRowId(row);
    if (!candidateIdSet.has(candidateId)) {
      return row;
    }

    return {
      ...row,
      match_source: "manual",
      stage: normalizedStage,
      candidate_stage: normalizedStage,
      rejected_by: isRejectedStage ? normalizedRejectedBy || null : null,
      rejection_reason: isRejectedStage ? normalizedRejectionReason || null : null,
      rejection_note: isRejectedStage ? normalizedRejectionNote || null : null,
    };
  });
};

const mergeCandidateRowsByCandidateId = (rows = [], updatedRows = []) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const updatesByCandidateId = new Map(
    (Array.isArray(updatedRows) ? updatedRows : [])
      .map((row) => [getCandidateRowId(row), row])
      .filter(([candidateId]) => Number.isFinite(candidateId) && candidateId > 0)
  );

  if (!updatesByCandidateId.size) {
    return safeRows;
  }

  const existingCandidateIds = new Set();
  const mergedRows = safeRows.map((row) => {
    const candidateId = getCandidateRowId(row);
    if (candidateId) existingCandidateIds.add(candidateId);
    const nextRow = updatesByCandidateId.get(candidateId);
    return nextRow ? { ...row, ...nextRow } : row;
  });

  // AI matching can return entirely new candidates, not just updates to rows
  // already loaded in the tab. Keep those rows visible immediately.
  const newRows = Array.from(updatesByCandidateId.entries())
    .filter(([candidateId]) => !existingCandidateIds.has(candidateId))
    .map(([, row]) => row);

  return [...mergedRows, ...newRows];
};

const normalizeComparableTextBlock = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\W_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const resolveDistinctResponsibilitiesText = (responsibilitiesValue, descriptionValue) => {
  const responsibilities = String(responsibilitiesValue || "").trim();
  if (!responsibilities) return "";

  const description = String(descriptionValue || "").trim();
  if (!description) return responsibilities;

  return normalizeComparableTextBlock(responsibilities) ===
    normalizeComparableTextBlock(description)
    ? ""
    : responsibilities;
};

const formatFileSize = (size) => {
  const num = Number(size);
  if (!Number.isFinite(num) || num <= 0) return "";
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${Math.round(num / 1024)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
};

const toDomain = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const normalized = raw.startsWith("http") ? raw : `https://${raw}`;
    return new URL(normalized).hostname.replace(/^www\./, "");
  } catch {
    return raw.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];
  }
};

const normalizeDocuments = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((doc) => {
      if (!doc || typeof doc !== "object") return null;

      const name =
        String(doc.name || doc.original_name || doc.filename || "Document.pdf").trim() ||
        "Document.pdf";
      const metaParts = [];

      const sizeLabel = formatFileSize(doc.size);
      if (sizeLabel) metaParts.push(sizeLabel);

      if (String(doc.mime_type || "").toLowerCase().includes("pdf") || /\.pdf$/i.test(name)) {
        metaParts.push("PDF");
      }

      if (doc.addedAt) {
        const addedLabel = formatLocalDate(doc.addedAt, {}, "");
        if (addedLabel) metaParts.push(addedLabel);
      }

      return {
        ...doc,
        name,
        url: String(doc.url || "").trim(),
        meta: metaParts.join(" - ") || "PDF",
      };
    })
    .filter(Boolean);
};

export default function JobDetailsView({
  job,
  initialTab = "details",
  detailFieldOptions,
  drawerMode = false,
  onTabChange,
  onJobChange,
}) {
  const location = useLocation();
  const currentUserRole = useMemo(() => getCurrentUserRole(), []);
  const isAdminUser = useMemo(() => ADMIN_ROLES.has(currentUserRole), [currentUserRole]);
  const canDeleteJobCandidates = useMemo(
    () => JOB_CANDIDATE_DELETE_ROLES.has(currentUserRole),
    [currentUserRole]
  );
  const [jobLive, setJobLive] = useState(job || null);
  const [status, setStatus] = useState(() => normalizeJobStatusDisplay(job?.status, "Open"));
  const [statusSaving, setStatusSaving] = useState(false);
  const [tab, setTab] = useProfileTab(initialTab, normalizeTab, onTabChange);
  const [jobStageValue, setJobStageValue] = useState(resolvePersistedJobStageValue(job || {}));
  const [stageFeedback, setStageFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [matchingCandidates, setMatchingCandidates] = useState(false);
  const [matchesRefreshKey, setMatchesRefreshKey] = useState(0);
  const [candidateRows, setCandidateRows] = useState([]);
  const [activeCandidateStatId, setActiveCandidateStatId] = useState(null);
  const [documentsUploading, setDocumentsUploading] = useState(false);
  const [documentsError, setDocumentsError] = useState("");
  const [sharingJob, setSharingJob] = useState(false);
  const sharingJobRef = useRef(false);
  const [sequenceDialogOpen, setSequenceDialogOpen] = useState(false);
  const [assignTeamDialogOpen, setAssignTeamDialogOpen] = useState(false);
  const [addCandidateDialogOpen, setAddCandidateDialogOpen] = useState(false);
  const [ownerDialogState, setOwnerDialogState] = useState({ open: false, mode: "add" });
  const onJobChangeRef = useRef(onJobChange);
  const jobLiveRef = useRef(jobLive);
  const jobRef = useRef(job);
  onJobChangeRef.current = onJobChange;
  jobLiveRef.current = jobLive;
  jobRef.current = job;
  const enrichmentRefreshTimeoutRef = useRef(null);
  const enrichmentRefreshAttemptsRef = useRef(0);
  const persistedJobStage = jobLive?.job_stage ?? job?.job_stage ?? "";
  const persistedPipelineStage = jobLive?.pipeline_stage ?? job?.pipeline_stage ?? "";
  const persistedStage = jobLive?.stage ?? job?.stage ?? "";
  const persistedStageValue = useMemo(
    () =>
      resolvePersistedJobStageValue({
        job_stage: persistedJobStage,
        pipeline_stage: persistedPipelineStage,
        stage: persistedStage,
      }),
    [persistedJobStage, persistedPipelineStage, persistedStage]
  );
  const routeCompanyId = useMemo(() => {
    const search = String(location?.search || "");
    if (!search) return null;

    const nextCompanyId = Number(new URLSearchParams(search).get("companyId") || 0);
    return Number.isFinite(nextCompanyId) && nextCompanyId > 0 ? nextCompanyId : null;
  }, [location?.search]);

  useEffect(() => {
    setJobLive((previous) => {
      if (previous?.id && job?.id && String(previous.id) === String(job.id)) {
        return previous;
      }
      return job || null;
    });
  // The prop object is intentionally excluded: table updates must not replace
  // the current job detail and its newly saved timeline entries.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  useEffect(() => {
    // The detail request is the authoritative value after an in-page update.
    // Prefer it over the list-row prop, which can still contain the prior
    // status until the parent state is synchronized.
    setStatus(normalizeJobStatusDisplay(jobLive?.status || job?.status, "Open"));
  }, [job?.status, jobLive?.status]);

  useEffect(() => {
    setJobStageValue(persistedStageValue);
  }, [persistedStageValue]);

  useEffect(() => {
    setCandidateRows([]);
    setActiveCandidateStatId(null);
  }, [job?.id]);

  const authFetch = useCallback(async (url, options = {}) => {
    const token = getToken();
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(token ? { "x-access-token": token } : {}),
    };
    return fetch(url, { ...options, headers });
  }, []);

  const jobDetailRequest = useRef(0);
  const jobMatchesRequest = useRef(0);
  const refreshJobDetails = useCallback(async () => {
    const requestId = ++jobDetailRequest.current;
    const jobId = jobLive?.id || job?.id;
    if (!jobId) return null;

    const res = await authFetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}`);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Failed to fetch job details (${res.status})`);
    }

    const payload = await res.json().catch(() => ({}));
    const nextJob = sanitizeJobCompanyContactFields(payload?.job || payload || {});
    if (nextJob && typeof nextJob === "object") {
      const mergedJob = { ...(jobLiveRef.current || jobRef.current || {}), ...nextJob };
      if (requestId !== jobDetailRequest.current) return null;
      setJobLive(mergedJob);
      onJobChangeRef.current?.(mergedJob);
      return mergedJob;
    }
    return nextJob;
  }, [authFetch, job?.id, jobLive?.id]);

  const refreshCandidateRows = useCallback(async () => {
    const requestId = ++jobMatchesRequest.current;
    const jobId = Number(jobLive?.id || job?.id || 0);
    if (!jobId) {
      setCandidateRows([]);
      return [];
    }

    const response = await authFetch(`${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/matches`, {
      cache: "no-store",
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      throw new Error(text || `Failed to fetch job candidates (${response.status})`);
    }

    const payload = await response.json().catch(() => ({}));
    const nextRows = Array.isArray(payload?.rows) ? payload.rows : [];
    if (requestId !== jobMatchesRequest.current) return [];
    setCandidateRows(nextRows);
    return nextRows;
  }, [authFetch, job?.id, jobLive?.id]);

  useEffect(() => {
    const refresh = () => {
      setMatchesRefreshKey(value => value + 1);
      refreshCandidateRows().catch(() => {});
      refreshJobDetails().catch(() => {});
    };
    window.addEventListener("rpo:activities-refresh", refresh);
    return () => window.removeEventListener("rpo:activities-refresh", refresh);
  }, [refreshCandidateRows, refreshJobDetails]);

  const clearPendingEnrichmentRefresh = useCallback(() => {
    if (enrichmentRefreshTimeoutRef.current !== null) {
      window.clearTimeout(enrichmentRefreshTimeoutRef.current);
      enrichmentRefreshTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!job?.id) return;

    refreshJobDetails().catch((err) => {
      console.error("Failed to load job details view data:", err);
    });
  }, [job?.id, refreshJobDetails]);

  useEffect(() => {
    const jobId = Number(jobLive?.id || job?.id || 0);
    if (!jobId) {
      setCandidateRows([]);
      return;
    }

    refreshCandidateRows().catch((err) => {
      console.error("Failed to load job candidate rows:", err);
      setCandidateRows([]);
    });
  }, [job?.id, jobLive?.id, refreshCandidateRows]);

  useEffect(() => {
    enrichmentRefreshAttemptsRef.current = 0;
    clearPendingEnrichmentRefresh();
  }, [job?.id, clearPendingEnrichmentRefresh]);

  useEffect(() => {
    const activeJob = jobLive || job || {};
    const enrichmentPending = isActivePendingFlag(
      activeJob?.job_details_enrichment_pending
    );

    syncJobBackgroundWork(activeJob);

    clearPendingEnrichmentRefresh();
    if (!job?.id || !enrichmentPending) {
      if (!enrichmentPending) {
        enrichmentRefreshAttemptsRef.current = 0;
      }
      return undefined;
    }

    if (enrichmentRefreshAttemptsRef.current >= 4) {
      return undefined;
    }

    enrichmentRefreshTimeoutRef.current = window.setTimeout(async () => {
      enrichmentRefreshTimeoutRef.current = null;
      enrichmentRefreshAttemptsRef.current += 1;

      try {
        const nextJob = await refreshJobDetails();
        if (!isActivePendingFlag(nextJob?.job_details_enrichment_pending)) {
          enrichmentRefreshAttemptsRef.current = 0;
        }
      } catch (err) {
        console.error("Failed to refresh pending job detail enrichment:", err);
      }
    }, 2500);

    return () => {
      clearPendingEnrichmentRefresh();
    };
  }, [job, job?.id, jobLive, refreshJobDetails, clearPendingEnrichmentRefresh]);

  const handleAddDocument = useCallback(
    async (file) => {
      const jobId = jobLive?.id || job?.id;
      if (!jobId || !file) return;

      const isPdf =
        String(file.type || "").toLowerCase() === "application/pdf" ||
        /\.pdf$/i.test(String(file.name || ""));

      if (!isPdf) {
        setDocumentsError("Only PDF files are supported.");
        return;
      }

      try {
        setDocumentsUploading(true);
        setDocumentsError("");

        const formData = new FormData();
        formData.append("documents", file);

        const res = await authFetch(
          `${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/documents`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!res.ok) {
          const text = await res.text().catch(() => "");
          throw new Error(text || `Failed to upload document (${res.status})`);
        }

        await refreshJobDetails();
      } catch (err) {
        console.error("Failed to upload job document:", err);
        setDocumentsError(err?.message || "Failed to upload job document.");
      } finally {
        setDocumentsUploading(false);
      }
    },
    [authFetch, job?.id, jobLive?.id, refreshJobDetails]
  );

  const handleSaveDetails = useCallback(
    async (draft = {}) => {
      const jobId = jobLive?.id || job?.id;
      if (!jobId) {
        throw new Error("Missing job id.");
      }

      const payload = {
        primary_contact: normalizeDraftTextValue(draft.primaryContact),
        contact_name: normalizeDraftTextValue(draft.primaryContact),
        hiring_manager_name: normalizeDraftTextValue(draft.primaryContact),
        title_field: normalizeDraftTextValue(draft.titleField),
        contact_title: normalizeDraftTextValue(draft.titleField),
        hiring_manager_title: normalizeDraftTextValue(draft.titleField),
        hiring_manager_role: normalizeDraftTextValue(draft.titleField),
        salary_range: normalizeDraftTextValue(draft.salaryRange),
        location: normalizeDraftTextValue(draft.location),
        locations: Array.isArray(draft.locationValues) ? draft.locationValues : [],
        industries: Array.isArray(draft.industryValues) ? draft.industryValues : [],
        industry_sectors_json: Array.isArray(draft.industryValues) ? draft.industryValues : [],
        employment_type: normalizeDraftTextValue(draft.employmentType),
        work_arrangement: normalizeDraftTextValue(draft.workArrangement),
        need_sponsorship: normalizeDraftTextValue(draft.needSponsorship),
        company_size: normalizeDraftTextValue(draft.companySize),
        size_range: normalizeDraftTextValue(draft.companySize),
        industry: normalizeDraftTextValue(draft.industry),
        company_industry: normalizeDraftTextValue(draft.industry),
        industry_sector: normalizeDraftTextValue(draft.industry),
        company_website: normalizeDraftTextValue(draft.companyWebsite),
        website_url: normalizeDraftTextValue(draft.companyWebsite),
        company_career_website: normalizeDraftTextValue(draft.careerWebsite),
        career_website: normalizeDraftTextValue(draft.careerWebsite),
        hard_skills: Array.isArray(draft.hardSkills) ? draft.hardSkills : [],
        soft_skills: Array.isArray(draft.softSkills) ? draft.softSkills : [],
        job_description: normalizeDraftTextValue(draft.jobDescription),
        description: normalizeDraftTextValue(draft.jobDescription),
        responsibilities: normalizeDraftTextValue(draft.responsibilities),
        qualifications: normalizeDraftTextValue(draft.qualifications),
      };

      const res = await authFetch(
        `${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/sequence-email-context`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(result?.error || `Failed to save job details (${res.status})`);
      }

      const updatedJob = sanitizeJobCompanyContactFields(result?.job || {});
      if (updatedJob && typeof updatedJob === "object" && updatedJob.id) {
        setJobLive((prev) => ({ ...(prev || {}), ...updatedJob }));
      }
      await refreshJobDetails();
    },
    [authFetch, job?.id, jobLive?.id, refreshJobDetails]
  );

  const handleAddCandidateToJob = useCallback(
    async (candidateRecord = {}, callbackContext = {}) => {
      const jobId = Number(jobLive?.id || job?.id || 0);
      const candidateId = Number(
        candidateRecord?.candidate_id ?? candidateRecord?.id ?? 0
      );
      const selectedStage =
        String(callbackContext?.jobStage || "").trim() || "Matched";
      if (!jobId) {
        throw new Error("Missing job id.");
      }
      if (!Number.isFinite(candidateId) || candidateId <= 0) {
        throw new Error("Missing candidate id.");
      }

      const response = await authFetch(
        `${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/matches`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            candidate_id: candidateId,
            stage: selectedStage,
          }),
        }
      );

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          payload?.error || `Failed to add candidate to job (${response.status})`
        );
      }

      await refreshCandidateRows();
      await refreshJobDetails().catch((error) => {
        console.error("Failed to refresh job after adding a candidate:", error);
      });

      setActiveCandidateStatId(null);
      setTab("stages");
      setMatchesRefreshKey((prev) => prev + 1);
      setStageFeedback({
        open: true,
        severity: "success",
        message:
          payload?.created === false
            ? `${
                candidateRecord?.full_name ||
                candidateRecord?.candidate_name ||
                "Candidate"
              } is already linked to this job. Stage set to ${selectedStage}.`
            : `${
                candidateRecord?.full_name ||
                candidateRecord?.candidate_name ||
                "Candidate"
              } added to this job in ${selectedStage}.`,
      });
    },
    [authFetch, job?.id, jobLive?.id, refreshCandidateRows, refreshJobDetails, setTab]
  );

  const handleMoveCandidatesToStage = useCallback(
    async (candidateIds = [], nextStage = "", moveOptions = {}) => {
      const jobId = Number(jobLive?.id || job?.id || 0);
      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      const normalizedStage = String(nextStage || "").trim();
      const normalizedRejectedBy = String(moveOptions?.rejectedBy || "").trim();
      const normalizedRejectionReason = String(
        moveOptions?.rejectionReason || ""
      ).trim();
      const normalizedRejectionNote = String(moveOptions?.rejectionNote || "").trim();

      if (!jobId) {
        throw new Error("Missing job id.");
      }

      if (!normalizedCandidateIds.length) {
        throw new Error("Select at least one candidate.");
      }

      if (!normalizedStage) {
        throw new Error("Select a stage.");
      }

      const previousCandidateRows = candidateRows;
      setCandidateRows((prev) =>
        applyStageToCandidateRows(prev, normalizedCandidateIds, normalizedStage, {
          rejectedBy: normalizedRejectedBy,
          rejectionReason: normalizedRejectionReason,
          rejectionNote: normalizedRejectionNote,
        })
      );

      try {
        const response = await authFetch(
          `${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/matches/stage`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              candidate_ids: normalizedCandidateIds,
              stage: normalizedStage,
              rejectedBy: normalizedRejectedBy,
              rejectionReason: normalizedRejectionReason,
              rejectionNote: normalizedRejectionNote,
              disposition: moveOptions.disposition,
              decisionBy: moveOptions.decisionBy,
            }),
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            payload?.error || `Failed to update candidate stage (${response.status})`
          );
        }

        setCandidateRows((prev) =>
          mergeCandidateRowsByCandidateId(prev, payload?.rows)
        );

        refreshCandidateRows().catch((error) => {
          console.error("Failed to refresh job candidates after moving stages:", error);
        });
        refreshJobDetails().catch((error) => {
          console.error("Failed to refresh job after moving candidates to a stage:", error);
        });
        setTab("stages");
        setStageFeedback({
          open: true,
          severity: "success",
          message: `Moved ${
            normalizedCandidateIds.length
          } candidate${normalizedCandidateIds.length === 1 ? "" : "s"} to ${normalizedStage}.`,
        });

        return payload;
      } catch (error) {
        console.error("Failed to move job candidates to a new stage:", error);
        setCandidateRows(previousCandidateRows);
        setStageFeedback({
          open: true,
          severity: "error",
          message: error?.message || "Failed to update candidate stage.",
        });
        throw error;
      }
    },
    [authFetch, candidateRows, job?.id, jobLive?.id, refreshCandidateRows, refreshJobDetails, setTab]
  );

  const handleDeleteCandidatesFromStage = useCallback(
    async (candidateIds = []) => {
      const jobId = Number(jobLive?.id || job?.id || 0);
      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );

      if (!canDeleteJobCandidates) {
        throw new Error(
          "Only recruiter or administrator users can delete candidates from this job."
        );
      }

      if (!jobId) {
        throw new Error("Missing job id.");
      }

      if (!normalizedCandidateIds.length) {
        throw new Error("Select at least one candidate.");
      }

      const previousCandidateRows = candidateRows;
      setCandidateRows((prev) =>
        prev.filter(
          (row) => !normalizedCandidateIds.includes(Number(row?.candidate_id || 0))
        )
      );

      try {
        const response = await authFetch(
          `${API_BASE}/api/jobs/${encodeURIComponent(jobId)}/matches`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              candidate_ids: normalizedCandidateIds,
            }),
          }
        );

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(
            payload?.error || `Failed to delete candidate from job (${response.status})`
          );
        }

        refreshCandidateRows().catch((error) => {
          console.error("Failed to refresh job candidates after deleting stage candidates:", error);
        });
        refreshJobDetails().catch((error) => {
          console.error("Failed to refresh job after deleting stage candidates:", error);
        });
        setActiveCandidateStatId(null);
        setTab("stages");
        setStageFeedback({
          open: true,
          severity: "success",
          message: `Deleted ${
            normalizedCandidateIds.length
          } candidate${normalizedCandidateIds.length === 1 ? "" : "s"} from this job.`,
        });

        return payload;
      } catch (error) {
        console.error("Failed to delete candidates from this job:", error);
        setCandidateRows(previousCandidateRows);
        setStageFeedback({
          open: true,
          severity: "error",
          message: error?.message || "Failed to delete candidates from this job.",
        });
        throw error;
      }
    },
    [jobLive?.id, job?.id, canDeleteJobCandidates, candidateRows, authFetch, refreshCandidateRows, refreshJobDetails, setTab]
  );

  const closeStageFeedback = useCallback(() => {
    setStageFeedback((prev) => ({ ...prev, open: false }));
  }, []);

  const handleJobStatusChange = useCallback(
    async (nextStatus) => {
      const jobId = Number(jobLive?.id || job?.id || 0);
      const normalizedStatus = normalizeJobStatusDisplay(nextStatus);
      const previousStatus = normalizeJobStatusDisplay(jobLive?.status || job?.status, "Open");

      if (!jobId || !normalizedStatus || statusSaving) {
        setStatus(previousStatus);
        return;
      }
      if (normalizedStatus === previousStatus) {
        setStatus(previousStatus);
        return;
      }

      setStatus(normalizedStatus);
      setStatusSaving(true);
      setStageFeedback((prev) => ({ ...prev, open: false }));

      try {
        const response = await authFetch(`${API_BASE}/api/jobs/bulk-status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: [jobId], status: normalizedStatus }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to save job status (${response.status})`);
        }

        // Fetch the detail payload after the server has written its activity,
        // so the visible Activities tab updates at the same time as the status.
        const refreshedJob = await refreshJobDetails();
        const savedStatus = normalizeJobStatusDisplay(
          refreshedJob?.status || payload?.status || normalizedStatus,
          normalizedStatus
        );
        setStatus(savedStatus || normalizedStatus);
        setStageFeedback({
          open: true,
          severity: "success",
          message: `Job status updated to ${savedStatus || normalizedStatus}.`,
        });
      } catch (error) {
        console.error("Failed to save job status:", error);
        setStatus(previousStatus);
        setStageFeedback({
          open: true,
          severity: "error",
          message: error?.message || "Failed to save job status.",
        });
      } finally {
        setStatusSaving(false);
      }
    },
    [authFetch, job?.id, jobLive?.id, jobLive?.status, job?.status, refreshJobDetails, statusSaving]
  );

  const handleMatchCandidates = useCallback(async () => {
    const jobId = Number(jobLive?.id || job?.id || 0);
    if (!jobId || matchingCandidates) return;

    setMatchingCandidates(true);

    try {
      const res = await authFetch(`${API_BASE}/api/jobs/match-candidates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          job_ids: [jobId],
        }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.error || `Failed to match candidates (${res.status})`);
      }

      const results = Array.isArray(payload?.results) ? payload.results : [];
      const currentResult =
        results.find((item) => Number(item?.job_id) === jobId) || results[0] || null;
      const matchedCount = Number(currentResult?.matched_count || 0);
      const topScore = Number(currentResult?.top_score || 0);

      setJobLive((prev) =>
        prev
          ? {
              ...prev,
              matches_count: matchedCount,
              matchesCount: matchedCount,
              best_match_percentage: topScore,
            }
          : prev
      );
      // Render the returned matches right away, then reconcile with the
      // database response below. This makes the Matches tab update without a
      // browser reload even when a proxy/browser still has an older response.
      setCandidateRows((previousRows) =>
        mergeCandidateRowsByCandidateId(
          previousRows,
          (Array.isArray(currentResult?.matches) ? currentResult.matches : []).map((match) => ({
            ...match,
            match_source: "ai",
          }))
        )
      );
      await refreshCandidateRows().catch((err) => {
        console.error("Failed to refresh candidate rows after AI matching:", err);
      });
      // The Matches tab contains all AI-ranked candidates. "Matched" is a
      // separate workflow stage, so filtering by it here hides valid newly
      // generated matches that are currently in another stage.
      setActiveCandidateStatId(null);
      setTab("matches");
      setMatchesRefreshKey((prev) => prev + 1);

      await refreshJobDetails().catch((err) => {
        console.error("Failed to refresh job details after candidate matching:", err);
      });

      if (!matchedCount) {
        window.alert("No candidate matches were generated for this job.");
        return;
      }

      window.alert(
        `Matched ${matchedCount} candidate${matchedCount === 1 ? "" : "s"} for ${
          currentResult?.job_title || jobLive?.job_title || job?.job_title || "this job"
        }.`
      );
    } catch (error) {
      console.error("Failed to match job candidates from job details:", error);
      window.alert(error?.message || "Failed to match candidates for this job.");
    } finally {
      setMatchingCandidates(false);
    }
  }, [authFetch, job?.id, job?.job_title, jobLive?.id, jobLive?.job_title, matchingCandidates, refreshCandidateRows, refreshJobDetails, setTab]);

  const aiCandidateRows = useMemo(
    () => candidateRows.filter((row) => isAiJobCandidateRow(row)),
    [candidateRows]
  );

  const manualCandidateRows = useMemo(
    () => candidateRows.filter((row) => isManualJobCandidateRow(row)),
    [candidateRows]
  );

  const handleAssignTeamSubmit = useCallback(
    async ({ teamMembers = [] } = {}) => {
      const jobId = Number(jobLive?.id || job?.id || 0);
      const nextOwner = String(Array.isArray(teamMembers) ? teamMembers[0] || "" : "").trim();
      if (!jobId || !nextOwner) return;

      try {
        const response = await authFetch(`${API_BASE}/api/jobs/bulk-assign-team-member`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ids: [jobId],
            assignee_name: nextOwner,
          }),
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to assign team member (${response.status})`);
        }

        await refreshJobDetails();
      } catch (error) {
        console.error("Failed to assign job owner:", error);
        window.alert(error?.message || "Failed to assign team member.");
      }
    },
    [authFetch, job?.id, jobLive?.id, refreshJobDetails]
  );

  const openOwnerDialog = useCallback((mode = "add") => {
    setOwnerDialogState({ open: true, mode });
  }, []);

  const closeOwnerDialog = useCallback(() => {
    setOwnerDialogState((prev) => ({ ...prev, open: false }));
  }, []);

  const handleOwnerSubmit = useCallback(
    async ({ ownerName = "", ownerRole = "", ownerUserId = null } = {}) => {
      const jobId = Number(jobLive?.id || job?.id || 0);
      const nextOwnerName = String(ownerName || "").trim();
      const nextOwnerRole = String(ownerRole || "").trim();
      if (!jobId) {
        throw new Error("Missing job id.");
      }
      if (!nextOwnerName) {
        throw new Error("Owner name is required.");
      }
      if (!nextOwnerRole) {
        throw new Error("Owner role is required.");
      }

      let resolvedOwnerUserId = Number(ownerUserId || 0);
      if (!Number.isFinite(resolvedOwnerUserId) || resolvedOwnerUserId <= 0) {
        const recruiterUsers = await fetchRecruiterUsers({ apiBase: API_BASE }).catch(() => []);
        const matchingRecruiter = recruiterUsers.find(
          (row) =>
            String(row?.name || "").trim().toLowerCase() === nextOwnerName.toLowerCase()
        );
        resolvedOwnerUserId =
          matchingRecruiter?.id != null && Number.isFinite(Number(matchingRecruiter.id))
            ? Number(matchingRecruiter.id)
            : 0;
      }

      const response = await authFetch(`${API_BASE}/api/jobs/bulk-assign-team-member`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ids: [jobId],
          assignee_name: nextOwnerName,
          assignee_user_id:
            Number.isFinite(resolvedOwnerUserId) && resolvedOwnerUserId > 0
              ? resolvedOwnerUserId
              : null,
          owner_role: nextOwnerRole,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || `Failed to save owner (${response.status})`);
      }

      await refreshJobDetails();
    },
    [authFetch, job?.id, jobLive?.id, refreshJobDetails]
  );

  const counts = useMemo(
    () => ({
      stages: candidateRows.filter((row) => isManualJobCandidateRow(row)).length,
      matches: candidateRows.filter((row) => isAiJobCandidateRow(row)).length,
      activities: Math.max(0, toFiniteCount(jobLive?.activitiesCount ?? jobLive?.activities_count)),
    }),
    [candidateRows, jobLive]
  );

  const tabs = useMemo(
    () => [
      { label: "Details", value: "details" },
      { label: "Stages", value: "stages", count: counts.stages },
      { label: "Matches", value: "matches", count: counts.matches },
      { label: "Activities", value: "activities", count: counts.activities },
    ],
    [counts]
  );

  const data = useMemo(() => {
    const activeJob = sanitizeJobCompanyContactFields(jobLive || job || {});
    const companyWebsite = activeJob?.company_website || activeJob?.website || "";
    const resolvedLocations = normalizeLocationFilterValues([
      activeJob?.locations,
      activeJob?.locations_json,
      activeJob?.locationsJson,
      activeJob?.location_values,
      activeJob?.location,
    ]);
    const hardSkills = normalizeStringArray(
      activeJob?.hard_skills || activeJob?.tech_stack_tokens || activeJob?.tech_stack
    );
    const softSkills = normalizeStringArray(activeJob?.soft_skills);
    const documents = normalizeDocuments(activeJob?.documents);
    const resolvedJobDescription =
      activeJob?.job_description || activeJob?.description || "";
    const resolvedResponsibilities = resolveDistinctResponsibilitiesText(
      activeJob?.responsibilities,
      resolvedJobDescription
    );
    const normalizedContacts = Array.isArray(activeJob?.contacts) ? activeJob.contacts : [];
    const primaryContactName =
      activeJob?.primary_contact ||
      activeJob?.contact_name ||
      activeJob?.hiring_manager_name ||
      "";
    const matchedPrimaryContact =
      normalizedContacts.find(
        (contact) =>
          normalizeComparableName(
            contact?.name || contact?.contact_name || contact?.hiring_manager_name || ""
          ) === normalizeComparableName(primaryContactName)
      ) ||
      normalizedContacts[0] ||
      null;

    return {
      jobId: activeJob?.id || job?.id || null,
      companyId: activeJob?.company_id || activeJob?.companyId || routeCompanyId || null,
      jobTitle: activeJob?.job_title || "Job",
      companyName: activeJob?.company_name || "Company",
      companyDomain: toDomain(companyWebsite),
      companyLogo: activeJob?.company_logo || "",
      primaryContact: primaryContactName,
      primaryContactEmail:
        activeJob?.contact_email ||
        matchedPrimaryContact?.email ||
        matchedPrimaryContact?.contact_email ||
        activeJob?.job_primary_contact_email ||
        activeJob?.company_primary_contact_email ||
        "",
      primaryContactPhone:
        matchedPrimaryContact?.phone ||
        matchedPrimaryContact?.contact_phone ||
        activeJob?.contact_phone ||
        activeJob?.phone ||
        "",
      primaryContactLinkedin:
        matchedPrimaryContact?.linkedin_url ||
        matchedPrimaryContact?.linkedin ||
        activeJob?.linkedin_url ||
        "",
      titleField:
        activeJob?.contact_title ||
        activeJob?.hiring_manager_title ||
        activeJob?.hiring_manager_role ||
        "Hiring Manager",
      salaryRange: activeJob?.salary_range || activeJob?.compensation || "",
      locations: resolvedLocations,
      locations_json: resolvedLocations,
      locationsJson: resolvedLocations,
      location: (resolvedLocations.length ? resolvedLocations.join(" | ") : "") || activeJob?.location || "",
      employmentType: formatEmploymentType(activeJob?.employment_type || activeJob?.employment || ""),
      workArrangement: activeJob?.work_arrangement || "",
      needSponsorship:
        activeJob?.need_sponsorship ||
        activeJob?.requires_sponsorship ||
        activeJob?.visa_sponsorship ||
        "",
      companySize: activeJob?.company_size || activeJob?.size_range || "",
      industries:
        activeJob?.industries ||
        activeJob?.industry_sectors_json ||
        [],
      industry:
        activeJob?.company_industry || activeJob?.industry || activeJob?.industry_sector || "",
      companyWebsite: companyWebsite || "",
      careerWebsite:
        activeJob?.company_career_website ||
        activeJob?.career_website ||
        activeJob?.post_link ||
        "",
      companyEmail: activeJob?.company_email || "",
      companyPhone: activeJob?.company_phone || "",
      linkedinUrl: activeJob?.linkedin_url || "",
      facebookUrl: activeJob?.facebook_url || "",
      twitterUrl: activeJob?.twitter_url || activeJob?.x_url || "",
      hardSkills: hardSkills,
      softSkills: softSkills,
      jobDescription: resolvedJobDescription,
      responsibilities: resolvedResponsibilities,
      qualifications: activeJob?.qualifications || "",
      documents,
      contacts: normalizedContacts,
      activities: Array.isArray(activeJob?.activities) ? activeJob.activities : [],
      notesText: String(activeJob?.notes || "").trim(),
    };
  }, [job, jobLive, routeCompanyId]);
  const aiCompanySeed = useMemo(
    () => ({
      name: String(data?.companyName || "").trim(),
      website: String(data?.companyWebsite || "").trim(),
      career_site: String(data?.careerWebsite || "").trim(),
      company_link: String(
        data?.companyWebsite || data?.careerWebsite || ""
      ).trim(),
      created_at: resolveEntityAddedDate(jobLive, job),
      updated_at: String(
        jobLive?.updated_at ||
          jobLive?.updatedAt ||
          job?.updated_at ||
          job?.updatedAt ||
          ""
      ).trim(),
      notes: data?.companyName
        ? `Create or refresh ${data.companyName}, scan the public website for company information, leadership, team members, and current openings.`
        : "",
    }),
    [data?.careerWebsite, data?.companyName, data?.companyWebsite, job, jobLive]
  );

  const ownerCardData = useMemo(() => {
    const activeJob = jobLive || job || {};
    return {
      ownerName: getRecruiterOwnerName(activeJob) || "-",
      ownerRole:
        String(
          activeJob?.owner_role ||
            activeJob?.assigned_team_member_role ||
            activeJob?.team_member_role ||
            activeJob?.owner_title ||
            "Team Manager"
        ).trim() || "Team Manager",
    };
  }, [job, jobLive]);

  const createdDateText = useMemo(
    () => formatEntityAddedDateText(resolveEntityAddedDate(jobLive, job)),
    [job, jobLive]
  );

  const stats = useMemo(() => {
    return JOB_CANDIDATE_STAT_ITEMS.map((item) => ({
      id: item.id,
      value: String(aiCandidateRows.filter((row) => isJobCandidateStatMatch(row, item.id)).length),
      label: item.label,
    }));
  }, [aiCandidateRows]);

  const shareDialogData = useMemo(() => {
    const activeJob = sanitizeJobCompanyContactFields(jobLive || job || {});
    const contactEmails = uniqTextValues(
      [
        activeJob?.email,
        activeJob?.contact_email,
        activeJob?.last_activity_email,
        activeJob?.job_primary_contact_email,
        activeJob?.company_primary_contact_email,
        ...(Array.isArray(data?.contacts) ? data.contacts : []).flatMap((contact) => [
          contact?.email,
          contact?.contact_email,
        ]),
      ].filter(Boolean)
    );
    const ownerNames = uniqTextValues([
      getRecruiterOwnerName(activeJob),
      activeJob?.owner_name,
      activeJob?.owner,
      activeJob?.assigned_team_member,
      activeJob?.team_member,
    ]);

    return {
      resetKey: `${data?.jobId || "job"}:${data?.companyId || "company"}`,
      summaryItems: uniqTextValues([
        data?.jobTitle,
        data?.companyName,
        data?.primaryContact,
        data?.location,
        data?.employmentType,
      ]),
      defaultTeamMembers: ownerNames.slice(0, 1),
      defaultEmailRecipients: contactEmails.slice(0, 1),
      emailSuggestions: contactEmails,
      notesDefault: String(
        activeJob?.job_description ||
          activeJob?.description ||
          activeJob?.responsibilities ||
          data?.jobDescription ||
          ""
      ).trim(),
    };
  }, [data, job, jobLive]);

  const sequenceItems = useMemo(() => {
    const activeJob = jobLive || job || {};
    const item = buildJobSequenceItem(
      {
        ...activeJob,
        id: data?.jobId || activeJob?.id,
        job_title: data?.jobTitle || activeJob?.job_title,
        company_name: data?.companyName || activeJob?.company_name,
        contact_email: data?.companyEmail || activeJob?.contact_email,
        contact_phone: data?.companyPhone || activeJob?.contact_phone,
        location: data?.location || activeJob?.location,
      },
      "job-details"
    );

    return item?.entityId ? [item] : [];
  }, [data, job, jobLive]);

  const ownerDialogData = useMemo(() => {
    const nextOwnerName =
      ownerCardData.ownerName && ownerCardData.ownerName !== "-" ? ownerCardData.ownerName : "";
    const nextOwnerRole =
      ownerCardData.ownerRole && ownerCardData.ownerRole !== "-" ? ownerCardData.ownerRole : "";

    return {
      resetKey: `${shareDialogData.resetKey}:owner:${ownerDialogState.mode}`,
      summaryItems: shareDialogData.summaryItems,
      defaultOwnerNames: nextOwnerName ? [nextOwnerName] : [],
      defaultOwnerRoles: nextOwnerRole ? [nextOwnerRole] : [],
      roleSuggestions: nextOwnerRole ? [nextOwnerRole] : [],
    };
  }, [
    ownerCardData.ownerName,
    ownerCardData.ownerRole,
    ownerDialogState.mode,
    shareDialogData.resetKey,
    shareDialogData.summaryItems,
  ]);

  const runLoggedQuickAction = useCallback(
    (_actionKey, _actionLabel, actionFn) => () => {
      actionFn?.();
    },
    []
  );

  const handleQuickActionAddCandidate = useCallback(() => {
    setAddCandidateDialogOpen(true);
  }, []);

  const handleQuickActionCandidateCreated = useCallback(
    async (candidateRecord = {}, callbackContext = {}) => {
      await handleAddCandidateToJob(candidateRecord, callbackContext);
    },
    [handleAddCandidateToJob]
  );

  const handleShareJob = useCallback(async () => {
    if (sharingJobRef.current) return;
    sharingJobRef.current = true;
    setSharingJob(true);
    try {
      await createAndCopyPublicShareLink({
        apiBase: API_BASE,
        payload: buildPublicSharePayload("job", { ...job, ...jobLive }),
      });
      setStageFeedback({ open: true, severity: "success", message: SHARE_LINK_COPIED_MESSAGE });
    } catch (error) {
      setStageFeedback({ open: true, severity: "error", message: error?.message || "Failed to create share link." });
    } finally {
      sharingJobRef.current = false;
      setSharingJob(false);
    }
  }, [job, jobLive]);

  const quickActionItems = useMemo(
    () => [
      { key: "schedule", label: "Schedule", icon: <EventOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />,
        onClick: () => openInterviewScheduler({ jobId: data.jobId }) },
      {
        key: "share",
        label: sharingJob ? "Sharing..." : "Share",
        onClick: handleShareJob,
        disabled: sharingJob || !data.jobId,
        icon: <IosShareOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />,
      },
      {
        key: "create-sequence",
        label: "Create Sequence",
        onClick: runLoggedQuickAction(
          "create_sequence",
          "Create Sequence",
          () => setSequenceDialogOpen(true)
        ),
        icon: <SequenceActionIcon size={16} />,
      },
      {
        key: "pitch-package",
        label: "Pitch Package",
        onClick: runLoggedQuickAction("pitch_package", "Pitch Package"),
        icon: <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "#fff" }} />,
      },
      {
        key: "add-candidate",
        label: "Add Candidate",
        onClick: handleQuickActionAddCandidate,
        icon: <PersonOutlineIcon sx={{ fontSize: 16, color: "#fff" }} />,
      },
      {
        key: "assign-team",
        label: "Assign Team",
        onClick: runLoggedQuickAction("assign_team", "Assign Team", () => setAssignTeamDialogOpen(true)),
        icon: <AssignTeamPillIcon />,
      },
      {
        key: "match-candidates",
        label: matchingCandidates ? "Matching..." : "Match Candidates",
        onClick: runLoggedQuickAction("match_candidates", "Match Candidates", handleMatchCandidates),
        icon: <MatchActionIcon size={16} />,
        disabled: matchingCandidates,
      },
    ],
    [
      handleMatchCandidates,
      handleShareJob,
      sharingJob,
      data.jobId,
      handleQuickActionAddCandidate,
      matchingCandidates,
      runLoggedQuickAction,
    ]
  );

  const openMatchesTab = useCallback(() => {
    setTab("matches");
  }, [setTab]);

  const handleSequenceCreated = useCallback(async () => {
    setSequenceDialogOpen(false);
    await refreshJobDetails();
  }, [refreshJobDetails]);

  const handleCandidateStatItemClick = useCallback((item) => {
    const nextId = String(item?.id || "").trim().toLowerCase();
    if (!nextId) return;

    setActiveCandidateStatId((prev) => (prev === nextId ? null : nextId));
    setTab("matches");
  }, [setTab]);

  return (
    <>
      <ListTableTabsTemplate
        rootSx={{ py: drawerMode ? 0 : 2 }}
        shellSx={{ pb: 0, pt: drawerMode ? 0 : undefined }}
        statsSection={
          drawerMode ? null : (
            <StatsSlider
              items={stats}
              autoPlay={false}
              showArrows={true}
              cardMinWidth={200}
              gap={12}
              onItemClick={handleCandidateStatItemClick}
              activeItemId={activeCandidateStatId}
            />
          )
        }
        headerSection={
          <ProfileHeader
            title={data.jobTitle}
            tabs={tabs}
            tab={tab}
            onTabChange={(v) => setTab(normalizeTab(v))}
            actionLabel="Status"
            actionValue={status}
            onActionChange={handleJobStatusChange}
            actionPlaceholder="Select status"
            actionPlaceholderValue=""
            actionOptions={JOB_STATUS_SELECT_OPTIONS}
            actionShowPlaceholderOption={false}
            actionDisabled={statusSaving}
            actionFeedback={stageFeedback}
            onActionFeedbackClose={closeStageFeedback}
            metaLabel="Owner"
            metaValue={ownerCardData.ownerName}
            dateText={createdDateText}
            sx={TAB_VIEW_HEADER_SX}
          />
        }
        contentSection={
          <>
            <EntityBackgroundTasks entity={jobLive || job || {}} />
            <JobDetailsContent
              tab={tab}
              data={data}
              fieldOptions={detailFieldOptions}
              stageFilter={jobStageValue}
              candidateFilter={activeCandidateStatId || "all"}
              stageRows={manualCandidateRows}
              onMoveCandidatesToStage={handleMoveCandidatesToStage}
              onDeleteCandidatesFromStage={handleDeleteCandidatesFromStage}
              onMoveMatchCandidatesToStage={handleMoveCandidatesToStage}
              onSaveDetails={handleSaveDetails}
              onAddDocument={handleAddDocument}
              documentsUploading={documentsUploading}
              documentsError={documentsError}
              prefetchedMatchRows={aiCandidateRows}
              matchesRefreshKey={matchesRefreshKey}
              isAdminUser={isAdminUser}
              canDeleteCandidates={canDeleteJobCandidates}
              currentUserRole={currentUserRole}
            />
          </>
        }
        contentWrapperSx={TAB_VIEW_CONTENT_WRAPPER_SX}
        rightSidebar={
          drawerMode ? null : (
            <>
              <QuickActionsCard
                actionItems={quickActionItems}
                titleSx={{
                  mb: 1.75,
                }}
                actionsContainerSx={{
                  gap: 1.5,
                }}
                buttonSx={{
                  height: 36,
                  px: 2,
                  fontSize: 12.5,
                  fontWeight: 500,
                  letterSpacing: 0,
                  boxShadow: "none",
                  "& .MuiButton-startIcon": {
                    mr: 0.75,
                    ml: 0,
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 16,
                  },
                }}
              />
              <AIAgentCard
                companySeed={aiCompanySeed}
                entityType="job"
                entityId={data?.jobId}
                entityName={data?.jobTitle}
                currentJobId={data?.jobId}
                currentCompanyId={data?.companyId}
                currentCompanyName={data?.companyName}
              />
              <JobTopMatchesCard
                apiBase={API_BASE}
                jobId={data.jobId}
                onViewAll={openMatchesTab}
                prefetchedRows={aiCandidateRows}
                refreshKey={matchesRefreshKey}
              />
              <JobOwnerCard
                ownerName={ownerCardData.ownerName}
                ownerRole={ownerCardData.ownerRole}
                onAddOwner={() => openOwnerDialog("add")}
                onEditOwner={() => openOwnerDialog("edit")}
              />
              <JobNotesCard
                apiBase={API_BASE}
                entityId={data.jobId}
                activities={data.activities}
                notesText={data.notesText}
                onRefresh={refreshJobDetails}
                onOpenAllNotes={() => setTab("activities")}
              />
            </>
          )
        }
        rightWidth={392}
        rightSidebarSx={{ flexShrink: 0 }}
        leftColumnSx={{
          borderRadius: drawerMode ? 0 : 3,
          border: `1px solid ${BORDER}`,
        }}
      />

      <AddToSequenceDialog
        open={sequenceDialogOpen}
        onClose={() => setSequenceDialogOpen(false)}
        entityType="job"
        items={sequenceItems}
        onCreated={handleSequenceCreated}
      />

      <ShareJobDialog
        open={assignTeamDialogOpen}
        onClose={() => setAssignTeamDialogOpen(false)}
        onSubmit={handleAssignTeamSubmit}
        apiBase={API_BASE}
        resetKey={`${shareDialogData.resetKey}:assign`}
        title="Assign Team Member"
        sectionTitle="Assign"
        submitLabel="Assign Team Member"
        showEmailField={false}
        documentLayout="inline"
        headerIcon={<TeamMemberAddIcon size={20} color="#3451F4" />}
        summaryItems={shareDialogData.summaryItems}
        defaultTeamMembers={shareDialogData.defaultTeamMembers}
        notesDefault={shareDialogData.notesDefault}
        defaultDocuments={data.documents}
      />

      <JobOwnerDialog
        open={ownerDialogState.open}
        onClose={closeOwnerDialog}
        onSubmit={handleOwnerSubmit}
        apiBase={API_BASE}
        resetKey={ownerDialogData.resetKey}
        title={ownerDialogState.mode === "edit" ? "Edit Owner" : "Add Owner"}
        submitLabel={ownerDialogState.mode === "edit" ? "Update Owner" : "Add New Owner"}
        summaryItems={ownerDialogData.summaryItems}
        defaultOwnerNames={ownerDialogData.defaultOwnerNames}
        defaultOwnerRoles={ownerDialogData.defaultOwnerRoles}
        roleSuggestions={ownerDialogData.roleSuggestions}
      />

      <AddCandidateDialog
        open={addCandidateDialogOpen}
        onClose={() => setAddCandidateDialogOpen(false)}
        onCreated={handleQuickActionCandidateCreated}
        apiBase={API_BASE}
        initialScreen="overview"
        openProfileOnComplete={false}
        requireOnCreatedSuccess={true}
        jobStageOptions={true}
        existingCandidateOnly={true}
        showStageFieldForExistingCandidate={true}
        existingCandidateSubmitLabel="Add Candidate"
      />
    </>
  );
}
